import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getCustomers, deleteCustomer, toggleCustomerStatus, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff, FaSearch, FaPrint, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { chunkArray } from '../../../../shared/utils.js/utils';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { saveCustomersCache, getCustomersCache, generateCustomersCacheKey, getAllCustomersCacheForQuery, clearOldCustomersCache, isOnline, removeCustomerFromCache } from '../../../../shared/utils.js/offlineQueue';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';

// Customers Page
const Customers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { customers: reduxCustomers, customersPagination: reduxCustomersPagination, isLoading, error, success } = useSelector((state) => state.auth);
  const [customers, setCustomers] = useState([]);
  const [customersPagination, setCustomersPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);       // القيمة الفعلية
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const searchTimeoutRef = useRef(null);
  const customersOfflineToastShownRef = useRef(false);
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = isRTL ? 'ar-EG' : 'en-US';
      return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    setTitle('sidenav.customers');
    clearOldCustomersCache();
  }, [setTitle]);

  const handleApplyPerPage = () => {
    const total = customersPagination?.total || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, perPageInput));
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  // Sync Redux customers into local state
  useEffect(() => {
    if (Array.isArray(reduxCustomers)) {
      setCustomers(reduxCustomers);
      setCustomersPagination(reduxCustomersPagination || null);
    }
  }, [reduxCustomers, reduxCustomersPagination]);

  // Flag to prevent concurrent fetches
  const isFetchingCustomersRef = useRef(false);
  const lastFetchKeyRef = useRef('');

  // Fetch customers with offline cache support
  const fetchCustomers = useCallback(async (page = currentPage, perPageValue = perPage) => {
    // Prevent concurrent fetches with same params
    const fetchKey = `${page}_${perPageValue}_${searchTerm}_${isActiveFilter}`;
    if (isFetchingCustomersRef.current && lastFetchKeyRef.current === fetchKey) {
      return; // Already fetching with same params
    }

    isFetchingCustomersRef.current = true;
    lastFetchKeyRef.current = fetchKey;

    try {
      const queryParams = {
        page,
        per_page: perPageValue,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      };
      const cacheKey = generateCustomersCacheKey(queryParams);

      // Step 1: Try to load from IndexedDB first
      let cachedData = null;
      try {
        cachedData = await getCustomersCache(cacheKey);
        if (cachedData && Array.isArray(cachedData.customers)) {
          setCustomers(cachedData.customers);
          setCustomersPagination(cachedData.pagination);
        }
      } catch (cacheError) {
        console.error('Error loading from cache:', cacheError);
      }

      // Step 2: If offline, use cached data for pagination
      if (!isOnline()) {
        const allCachedCustomers = await getAllCustomersCacheForQuery({
          per_page: perPageValue,
          search: searchTerm || undefined,
          is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
        });

        if (Array.isArray(allCachedCustomers) && allCachedCustomers.length > 0) {
          const total = allCachedCustomers.length;
          const lastPage = Math.max(1, Math.ceil(total / perPageValue));
          const safePage = Math.min(page, lastPage);
          const startIndex = (safePage - 1) * perPageValue;
          const pageItems = allCachedCustomers.slice(startIndex, startIndex + perPageValue);

          setCustomers(pageItems);
          setCustomersPagination({
            current_page: safePage,
            last_page: lastPage,
            per_page: perPageValue,
            total,
          });

          if (!customersOfflineToastShownRef.current) {
            customersOfflineToastShownRef.current = true;
            toast.info(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت. يتم عرض العملاء المحفوظة محلياً.'
                : 'No internet connection. Showing locally saved customers.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
          return;
        } else {
          if (!customersOfflineToastShownRef.current) {
            customersOfflineToastShownRef.current = true;
            toast.warning(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة محلياً.'
                : 'No internet connection and no locally saved data available.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
          setCustomers([]);
          setCustomersPagination(null);
          return;
        }
      }

      // Step 3: Online - fetch from API
      try {
        const params = { page, per_page: perPageValue };
        if (searchTerm) params.search = searchTerm;
        if (isActiveFilter !== '') params.is_active = isActiveFilter;

        const result = await dispatch(getCustomers(params));
        if (getCustomers.fulfilled.match(result)) {
          const response = result.payload;
          const data = response?.data || response;
          let customersList = [];
          let paginationMeta = null;

          if (Array.isArray(data?.data)) {
            customersList = data.data;
            paginationMeta = data.meta || null;
          } else if (Array.isArray(data)) {
            customersList = data;
            paginationMeta = null;
          }

          // Save to cache
          try {
            await saveCustomersCache(cacheKey, customersList, paginationMeta);
          } catch (cacheError) {
            console.error('Error saving customers cache:', cacheError);
          }

          setCustomers(customersList);
          setCustomersPagination(paginationMeta);
          customersOfflineToastShownRef.current = false; // Reset for next offline session
        }
      } catch (apiError) {
        // API failed, try cache
        const allCachedCustomers = await getAllCustomersCacheForQuery({
          per_page: perPageValue,
          search: searchTerm || undefined,
          is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
        });

        if (Array.isArray(allCachedCustomers) && allCachedCustomers.length > 0) {
          const total = allCachedCustomers.length;
          const lastPage = Math.max(1, Math.ceil(total / perPageValue));
          const safePage = Math.min(page, lastPage);
          const startIndex = (safePage - 1) * perPageValue;
          const pageItems = allCachedCustomers.slice(startIndex, startIndex + perPageValue);

          setCustomers(pageItems);
          setCustomersPagination({
            current_page: safePage,
            last_page: lastPage,
            per_page: perPageValue,
            total,
          });

          toast.warning(
            isRTL
              ? 'لا يمكن الاتصال بالخادم. يتم عرض العملاء المحفوظة محلياً.'
              : 'Cannot connect to server. Showing locally saved customers.',
            { rtl: isRTL, autoClose: 3000 }
          );
        } else {
          setCustomers([]);
          setCustomersPagination(null);
          toast.error(
            apiError?.message || (isRTL ? 'فشل تحميل العملاء' : 'Failed to load customers'),
            { rtl: isRTL }
          );
        }
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      isFetchingCustomersRef.current = false;
    }
  }, [dispatch, searchTerm, isActiveFilter, currentPage, perPage, isRTL]);

  useEffect(() => {
    fetchCustomers(currentPage, perPage);
  }, [currentPage, perPage, searchTerm, isActiveFilter, fetchCustomers]);

  // Auto-refresh when coming back online (with debounce to prevent loops)
  useEffect(() => {
    let refreshTimeout = null;
    let lastRefreshTime = 0;
    const REFRESH_DEBOUNCE = 3000; // 3 seconds debounce to prevent too frequent refreshes

    const debouncedRefresh = () => {
      // Don't refresh if already fetching
      if (isFetchingCustomersRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastRefreshTime < REFRESH_DEBOUNCE) {
        // Cancel previous timeout and set new one
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        refreshTimeout = setTimeout(() => {
          if (!isFetchingCustomersRef.current) {
            fetchCustomers(currentPage, perPage);
            lastRefreshTime = Date.now();
          }
        }, REFRESH_DEBOUNCE - (now - lastRefreshTime));
      } else {
        // Refresh immediately if enough time has passed and not already fetching
        if (!isFetchingCustomersRef.current) {
          fetchCustomers(currentPage, perPage);
          lastRefreshTime = now;
        }
      }
    };

    const handleOnline = () => {
      debouncedRefresh();
    };

    const handleOfflineRequestsRetried = (event) => {
      // Only refresh if there were actual retries (count > 0)
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        // Only refresh if at least one request was successfully retried
        const hasSuccessfulRetries = detail.results.some(r => r.success === true);
        if (hasSuccessfulRetries) {
          // Check if any of the successful retries were DELETE requests for customers
          const hasCustomerDelete = detail.results.some(r =>
            r.success === true &&
            r.request?.method === 'DELETE' &&
            r.request?.url?.includes('/customers/')
          );
          if (hasCustomerDelete) {
            // Refresh customers list to reflect deleted items
            debouncedRefresh();
          }
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offlineRequestsRetried', handleOfflineRequestsRetried);

    return () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offlineRequestsRetried', handleOfflineRequestsRetried);
    };
  }, [fetchCustomers, currentPage, perPage]);

  // Use customers directly from API (no local filtering needed - API handles it)
  const paginatedCustomers = customers || [];
  const paginationInfo = customersPagination || { current_page: currentPage, last_page: 1, total: 0 };
  const totalPages = paginationInfo.last_page || 1;
  const currentPageSafe = paginationInfo.current_page || currentPage;

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      // Only refresh when adding new customer
      if (success.includes('إضافة') || success.includes('تم إضافة') || success.includes('Add') || success.includes('added')) {
        fetchCustomers(currentPage, perPage);
      }
      // Don't refresh on delete or toggle status - they update optimistically
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (e) => {
    setIsActiveFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleDelete = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    const idToDelete = customerToDelete.id;
    const customerToDeleteBackup = customerToDelete;
    setShowDeleteModal(false);
    setCustomerToDelete(null);

    // Optimistically remove from local state first
    const updatedCustomers = customers.filter(customer => customer.id !== idToDelete);
    setCustomers(updatedCustomers);

    try {
      // Prepare query params for cache update
      const queryParams = {
        page: currentPage,
        per_page: perPage,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      };
      const cacheKey = generateCustomersCacheKey(queryParams);

      try {
        // Try to use offline request manager first
        const result = await makeOfflineRequest(`/customers/${idToDelete}`, 'DELETE', null);
        toast.success(t('customers.messages.deleteSuccess', { defaultValue: 'تم حذف العميل بنجاح' }), { rtl: isRTL });

        // Update pagination after successful deletion
        if (customersPagination && customersPagination.total > 0) {
          setCustomersPagination({
            ...customersPagination,
            total: Math.max(0, customersPagination.total - 1),
          });
        }

        // Update IndexedDB cache after successful deletion
        await saveCustomersCache(cacheKey, updatedCustomers, customersPagination ? { ...customersPagination, total: Math.max(0, customersPagination.total - 1) } : null);

        // Don't refresh - data is already updated optimistically
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          // Remove customer from all cache entries optimistically
          try {
            const removedCount = await removeCustomerFromCache(idToDelete);
          } catch (cacheError) {
            console.error('Error removing customer from cache:', cacheError);
          }

          // Update pagination after removal
          if (customersPagination && customersPagination.total > 0) {
            setCustomersPagination({
              ...customersPagination,
              total: Math.max(0, customersPagination.total - 1),
            });
          }

          toast.info(
            isRTL
              ? 'سيتم حذف العميل عند عودة الاتصال بالإنترنت'
              : 'Customer will be deleted when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );

          // Don't refresh - data is already updated optimistically
          return;
        }

        // If it's a client error (4xx), don't retry with Redux - just throw the error
        // This prevents duplicate requests and duplicate toast messages
        if (offlineError.status >= 400 && offlineError.status < 500) {
          throw offlineError; // Re-throw to be caught by outer catch block
        }

        // Only use Redux dispatch as fallback for network errors or server errors (5xx)
        // This should rarely happen since makeOfflineRequest handles most cases
        try {
          await dispatch(deleteCustomer(idToDelete)).unwrap();
          toast.success(t('customers.messages.deleteSuccess', { defaultValue: 'تم حذف العميل بنجاح' }), { rtl: isRTL });

          // Update pagination after successful deletion
          if (customersPagination && customersPagination.total > 0) {
            setCustomersPagination({
              ...customersPagination,
              total: Math.max(0, customersPagination.total - 1),
            });
          }

          // Update IndexedDB cache after successful deletion
          await saveCustomersCache(cacheKey, updatedCustomers, customersPagination ? { ...customersPagination, total: Math.max(0, customersPagination.total - 1) } : null);

          // Don't refresh - data is already updated optimistically
        } catch (reduxError) {
          // If Redux also fails, throw to outer catch
          throw reduxError;
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setCustomers(prev => {
        const exists = prev.find(customer => customer.id === idToDelete);
        if (!exists) {
          return [...prev, customerToDeleteBackup].sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        }
        return prev;
      });

      // Only show error if it's not already handled by Redux and not OFFLINE_REQUEST_SAVED
      // Redux will set error state and useEffect will show toast for Redux errors
      // For client errors (4xx) from makeOfflineRequest, show error here
      if (error?.message !== 'OFFLINE_REQUEST_SAVED') {
        // Check if error is from Redux (has response property from axios)
        const isReduxError = error?.response?.data !== undefined && error?.config !== undefined;

        if (!isReduxError) {
          // Error from makeOfflineRequest - show message from response
          // The error message already contains the API response message
          const errorMessage = error?.message || t('customers.messages.deleteError', { defaultValue: 'فشل حذف العميل' });
          toast.error(errorMessage, { rtl: isRTL });
        }
        // If it's a Redux error, useEffect will handle showing the toast
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      // Find the customer to get current status
      const customer = customers.find(c => c.id === id);
      if (!customer) return;

      const newIsActive = !customer.is_active;
      const originalIsActive = customer.is_active;

      // Optimistically update local state first
      const updatedCustomers = customers.map(c =>
        c.id === id ? { ...c, is_active: newIsActive } : c
      );
      setCustomers(updatedCustomers);

      // Prepare query params for cache update
      const queryParams = {
        page: 1,
        per_page: 1000,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      };
      const cacheKey = generateCustomersCacheKey(queryParams);

      // Helper function to update all cache entries containing this customer
      const updateAllCustomerCaches = async (customerId, updatedStatus) => {
        try {
          // Get all cached customers
          const allCachedCustomers = await getAllCustomersCacheForQuery({
            per_page: 1000,
            search: searchTerm || undefined,
            is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
          });
          const updatedAllCustomers = allCachedCustomers.map(c =>
            c.id === customerId ? { ...c, is_active: updatedStatus } : c
          );

          // Update cache
          await saveCustomersCache(cacheKey, updatedCustomers, {
            current_page: 1,
            last_page: 1,
            per_page: updatedCustomers.length,
            total: updatedCustomers.length,
          });
        } catch (cacheError) {
          console.error('Error updating all customer caches:', cacheError);
        }
      };

      try {
        // Try to use offline request manager first
        // Note: toggle-status endpoint uses PATCH with empty body
        const result = await makeOfflineRequest(`/customers/${id}/toggle-status`, 'PATCH', null);
        toast.success(t('customers.messages.statusUpdated', { defaultValue: 'تم تحديث الحالة بنجاح' }), { rtl: isRTL });

        // Update all IndexedDB cache entries
        await updateAllCustomerCaches(id, newIsActive);
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'سيتم تحديث الحالة عند عودة الاتصال بالإنترنت'
              : 'Status will be updated when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          // Update IndexedDB cache optimistically
          await updateAllCustomerCaches(id, newIsActive);
          return;
        }

        // If it's a different error, try the original Redux dispatch as fallback
        await dispatch(toggleCustomerStatus({ id, newIsActive, originalIsActive })).unwrap();
        toast.success(t('customers.messages.statusUpdated', { defaultValue: 'تم تحديث الحالة بنجاح' }), { rtl: isRTL });

        // Update all IndexedDB cache entries
        await updateAllCustomerCaches(id, newIsActive);
      }
    } catch (error) {
      // Revert optimistic update on error
      const customer = customers.find(c => c.id === id);
      if (customer) {
        setCustomers(prev => prev.map(c =>
          c.id === id ? { ...c, is_active: !customer.is_active } : c
        ));
      }
      const errorMessage = typeof error === 'string'
        ? error
        : error?.message || error?.response?.data?.message || t('customers.messages.statusUpdateError', { defaultValue: 'فشل تحديث الحالة' });
      toast.error(errorMessage, { rtl: isRTL });
    }
  };

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (searchTerm) {
      parts.push(`${t('common.search', { defaultValue: 'Search' })}: ${searchTerm}`);
    }
    if (isActiveFilter === '1') {
      parts.push(`${t('customers.tableHeaders.status', { defaultValue: 'Status' })}: ${t('customers.active', { defaultValue: 'Active' })}`);
    } else if (isActiveFilter === '0') {
      parts.push(`${t('customers.tableHeaders.status', { defaultValue: 'Status' })}: ${t('customers.inactive', { defaultValue: 'Inactive' })}`);
    }
    return parts.join(' | ');
  }, [searchTerm, isActiveFilter, t]);

  // ─── Dynamic PDF Pagination Engine ──────────────────────────────────────────
  // Measures actual DOM element heights from the off-screen measurement template,
  // then iterates over ALL items to assign them to pages. A new page is created
  // only when there is no longer enough vertical space for the next row — no fixed
  // row counts, no magic numbers.
  const buildDynamicPdfChunks = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      setPdfChunks([]);
      return [];
    }

    const topHeaderEl = document.getElementById('customers-pdf-measure-top-header');
    const theadEl = document.getElementById('customers-pdf-measure-thead');
    const footerEl = document.getElementById('customers-pdf-measure-footer');
    const tbodyEl = document.getElementById('customers-pdf-measure-tbody');
    const sampleRowEl = tbodyEl?.querySelector('tr');

    const PAGE_H = 1043; // PdfPageInner container height (from hidden print template)
    const PAD_TOP = 18;  // PdfPageInner top padding
    const PAD_BOT = 10;  // footer's `bottom: 10px` offset from PdfPageInner bottom
    const SAFE = 15;  // extra buffer for multi-line rows

    const headerH = topHeaderEl?.offsetHeight || 180;
    const theadH = theadEl?.offsetHeight || 32;
    const footerH = footerEl?.offsetHeight || 120;
    const rowH = sampleRowEl?.offsetHeight || 28;

    // Y-coordinate where the footer begins — rows must end before this
    const footerStart = PAGE_H - PAD_BOT - footerH - SAFE;
    // Y-coordinate where the first data row of each page starts
    const pageStart = PAD_TOP + headerH + theadH;

    const chunks = [];
    let currentItems = [];
    let startIndex = 0;
    let currentY = pageStart;

    for (let i = 0; i < items.length; i++) {
      if (footerStart - currentY >= rowH) {
        // Row fits on current page
        currentItems.push(items[i]);
        currentY += rowH;
      } else {
        // No space — push current page and start a new one
        if (currentItems.length > 0) {
          chunks.push({ items: currentItems, startIndex });
        }
        currentItems = [items[i]];
        startIndex = i;
        currentY = pageStart + rowH;
      }
    }

    if (currentItems.length > 0) {
      chunks.push({ items: currentItems, startIndex });
    }

    // Safety fallback: if measurement returned nothing (e.g. elements not yet mounted)
    if (chunks.length === 0) {
      const fallbackSize = Math.max(5, Math.floor((footerStart - pageStart) / rowH));
      const fallbackChunks = chunkArray(items, fallbackSize).map((c, idx) => ({
        items: c,
        startIndex: idx * fallbackSize,
      }));
      setPdfChunks(fallbackChunks);
      return fallbackChunks;
    }

    setPdfChunks(chunks);
    return chunks;
  };

  // Handle print single customer - Download PDF
  const handlePrintCustomer = async (customer) => {
    if (!customer) return;

    try {
      setIsGeneratingPdf(true);
      setPdfChunks([{ items: [customer], startIndex: 0 }]);
      setPdfType('single');
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Shared PDF Engine — capture + assembly
      // (single customer renders through the same chunk-0 template)
      const identifier = customer.code || customer.id;
      await generateSingleElementPdf({
        elementId: 'customers-pdf-all-0',
        fileName: `ACE_Customer_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('customers.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error('Customer PDF error:', err);
      toast.error(t('customers.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle preview all customers
  const handlePreviewAll = async () => {
    try {
      const allCachedCustomers = await getAllCustomersCacheForQuery({
        per_page: 1000,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      });

      let allCustomersForPreview = [];
      if (isOnline()) {
        try {
          const total = customersPagination?.total || allCachedCustomers.length || 0;
          if (total === 0 && allCachedCustomers.length === 0) {
            toast.error(t('customers.preview.noData', { defaultValue: 'No data available for preview' }), { rtl: isRTL });
            return;
          }
          const params = {
            page: 1,
            per_page: total || allCachedCustomers.length || 1000,
          };
          if (searchTerm) params.search = searchTerm;
          if (isActiveFilter !== '') params.is_active = isActiveFilter;
          const result = await dispatch(getCustomers(params));
          if (getCustomers.fulfilled.match(result)) {
            const response = result.payload;
            const data = response?.data || response;
            if (Array.isArray(data?.data)) {
              allCustomersForPreview = data.data;
            } else if (Array.isArray(data)) {
              allCustomersForPreview = data;
            }
          }
        } catch (apiError) {
          if (allCachedCustomers.length > 0) {
            allCustomersForPreview = allCachedCustomers;
          } else if (customers && customers.length > 0) {
            allCustomersForPreview = customers;
          }
        }
      } else {
        if (allCachedCustomers.length > 0) {
          allCustomersForPreview = allCachedCustomers;
        } else if (customers && customers.length > 0) {
          allCustomersForPreview = customers;
        }
      }

      if (!allCustomersForPreview || allCustomersForPreview.length === 0) {
        toast.error(t('customers.preview.noData', { defaultValue: 'No data available for preview' }), { rtl: isRTL });
        return;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
      const chunks = buildDynamicPdfChunks(allCustomersForPreview);
      if (!chunks || chunks.length === 0) return;
      setPdfType('all');
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      setIsPreviewOpen(true);
    } catch (err) {
      console.error('Preview error:', err);
      toast.error(t('customers.preview.loadError', { defaultValue: 'Failed to load data' }), { rtl: isRTL });
    }
  };

  // Handle preview single customer
  const handlePreviewCustomer = async (customer) => {
    if (!customer) {
      toast.error(t('customers.preview.noData', { defaultValue: 'No data available for this customer' }), { rtl: isRTL });
      return;
    }
    setPdfChunks([{ items: [customer], startIndex: 0 }]);
    setPdfType('single');
    setIsPreviewOpen(true);
  };

  // Unified PDF capture: prints whatever is already rendered in the hidden templates
  // Used by the preview modal's Print button for both single and all records.
  // Avoids re-fetching data, ensuring Preview === Print with zero divergence.
  const handlePrintFromPreview = async () => {
    try {
      setIsGeneratingPdf(true);
      // Allow React to commit any pending hidden-template renders before capturing
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const currentChunks = pdfChunks;
      const customer0 = currentChunks[0]?.items[0];
      const filenameBase =
        pdfType === 'single'
          ? `ACE_Customer_${customer0?.code || customer0?.id}`
          : 'ACE_Customers_All';
      const pageElementIds = currentChunks.map((_, i) => `customers-pdf-all-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `${filenameBase}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('customers.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error('PDF print error:', err);
      toast.error(t('customers.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
      setIsPreviewOpen(false);
    }
  };

  // Handle print all customers
  const handlePrintAll = async () => {
    try {
      const allCachedCustomers = await getAllCustomersCacheForQuery({
        per_page: 1000,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      });

      let allCustomersForPrint = [];
      if (isOnline()) {
        try {
          const total = customersPagination?.total || allCachedCustomers.length || 0;
          if (total === 0 && allCachedCustomers.length === 0) {
            toast.error(t('customers.print.noData', { defaultValue: 'No data available for printing' }), { rtl: isRTL });
            return;
          }
          const params = {
            page: 1,
            per_page: total || allCachedCustomers.length || 1000,
          };
          if (searchTerm) params.search = searchTerm;
          if (isActiveFilter !== '') params.is_active = isActiveFilter;
          const result = await dispatch(getCustomers(params));
          if (getCustomers.fulfilled.match(result)) {
            const response = result.payload;
            const data = response?.data || response;
            if (Array.isArray(data?.data)) {
              allCustomersForPrint = data.data;
            } else if (Array.isArray(data)) {
              allCustomersForPrint = data;
            }
          }
        } catch (apiError) {
          if (allCachedCustomers.length > 0) {
            allCustomersForPrint = allCachedCustomers;
          } else if (customers && customers.length > 0) {
            allCustomersForPrint = customers;
          }
        }
      } else {
        if (allCachedCustomers.length > 0) {
          allCustomersForPrint = allCachedCustomers;
        } else if (customers && customers.length > 0) {
          allCustomersForPrint = customers;
        }
      }

      setIsGeneratingPdf(true);
      const chunks = buildDynamicPdfChunks(allCustomersForPrint);
      setPdfType('all');

      await new Promise((resolve) => setTimeout(resolve, 800));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) => `customers-pdf-all-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_Customers_All_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('customers.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Customers PDF error:', error);
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single customer to Excel
  const handleExportCustomerToExcel = (customer) => {
    const data = [{
      [t('customers.tableHeaders.code')]: customer.code || customer.id || '—',
      [t('customers.tableHeaders.nameAr')]: customer.name_ar || '—',
      [t('customers.tableHeaders.nameEn')]: customer.name_en || '—',
      [t('customers.tableHeaders.contactPerson')]: customer.contact_person || '—',
      [t('customers.tableHeaders.email')]: customer.email || '—',
      [t('customers.tableHeaders.phone')]: customer.phone || '—',
      [t('customers.tableHeaders.status')]: customer.is_active ? t('customers.active') : t('customers.inactive'),
      [t('customers.tableHeaders.createdAt')]: formatDate(customer.created_at)
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('customers.export.customerSheetName'));
    const identifier = customer.code || customer.id;
    const fileName = `${t('customers.export.customerSheetName')}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('customers.export.success'), { rtl: isRTL });
  };

  // Handle export all customers to Excel
  const handleExportAllToExcel = async () => {
    try {
      // Try to get all customers from cache first
      const allCachedCustomers = await getAllCustomersCacheForQuery({
        per_page: 1000,
        search: searchTerm || undefined,
        is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
      });

      let allCustomersForExport = [];
      if (isOnline()) {
        try {
          // Get total from pagination
          const total = customersPagination?.total || allCachedCustomers.length || 0;

          if (total === 0 && allCachedCustomers.length === 0) {
            toast.error(t('customers.export.noData', { defaultValue: 'No data to export' }), { rtl: isRTL });
            return;
          }

          // Build params to fetch all data
          const params = {
            page: 1,
            per_page: total || allCachedCustomers.length || 1000,
          };
          if (searchTerm) params.search = searchTerm;
          if (isActiveFilter !== '') params.is_active = isActiveFilter;

          // Fetch all customers
          const result = await dispatch(getCustomers(params));
          if (getCustomers.fulfilled.match(result)) {
            const response = result.payload;
            const data = response?.data || response;
            if (Array.isArray(data?.data)) {
              allCustomersForExport = data.data;
            } else if (Array.isArray(data)) {
              allCustomersForExport = data;
            }
          }
        } catch (apiError) {
          // API failed, use cached data if available
          if (allCachedCustomers.length > 0) {
            allCustomersForExport = allCachedCustomers;
          } else if (customers && customers.length > 0) {
            allCustomersForExport = customers;
          }
        }
      } else {
        // Offline: use cached data
        if (allCachedCustomers.length > 0) {
          allCustomersForExport = allCachedCustomers;
        } else if (customers && customers.length > 0) {
          allCustomersForExport = customers;
        }
      }

      if (!allCustomersForExport || allCustomersForExport.length === 0) {
        toast.error(t('customers.export.noData', { defaultValue: 'No data to export' }), { rtl: isRTL });
        return;
      }

      const data = allCustomersForExport.map((customer, index) => ({
        [t('common.serialNumber', { defaultValue: '#' })]: index + 1,
        [t('customers.tableHeaders.code')]: customer.code || customer.id || '—',
        [t('customers.tableHeaders.nameAr')]: customer.name_ar || '—',
        [t('customers.tableHeaders.nameEn')]: customer.name_en || '—',
        [t('customers.tableHeaders.contactPerson')]: customer.contact_person || '—',
        [t('customers.tableHeaders.email')]: customer.email || '—',
        [t('customers.tableHeaders.phone')]: customer.phone || '—',
        [t('customers.tableHeaders.status')]: customer.is_active ? t('customers.active') : t('customers.inactive'),
        [t('customers.tableHeaders.createdAt')]: formatDate(customer.created_at)
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('customers.export.customersSheetName'));
      const fileName = `${t('customers.export.customersSheetName')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('customers.export.success', { defaultValue: 'Data exported successfully' }), { rtl: isRTL });
    } catch (err) {
      console.error('Error exporting customers:', err);
      toast.error(t('customers.export.error', { defaultValue: 'Failed to export data' }), { rtl: isRTL });
    }
  };

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{t('customers.title')}</h1>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {t('common.perPage', { defaultValue: 'Per Page' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, perPageInput - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={customersPagination?.total || 1000}
                value={perPageInput}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 1;
                  const maxValue = customersPagination?.total || 1000;
                  setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                }}
                style={{
                  width: '70px',
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderRadius: 0,
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
              />
              <button
                onClick={() => {
                  const maxValue = customersPagination?.total || 1000;
                  const newValue = Math.min(maxValue, perPageInput + 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaEye /> {t('customers.preview.all')}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse',
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint /> {isGeneratingPdf ? t('customers.print.generating') : t('customers.print.all')}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaFileExcel /> {t('customers.export.all')}
          </button>
          <button
            onClick={() => navigate('/customers/add')}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaPlus /> {t('customers.addNew')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
          <FaSearch style={{
            position: 'absolute',
            right: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666',
            fontSize: '16px'
          }} />
          <input
            type="text"
            placeholder={t('customers.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '12px 45px 12px 15px',
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
          />
        </div>
        <select
          value={isActiveFilter}
          onChange={handleFilterChange}
          style={{
            padding: '12px 15px',
            backgroundColor: '#1b3350',
            border: '1px solid #333',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            minWidth: '150px',
            cursor: 'pointer',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          <option value="">{t('customers.allStatuses')}</option>
          <option value="1">{t('customers.active')}</option>
          <option value="0">{t('customers.inactive')}</option>
        </select>
      </div>

      <div style={{
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {isLoading && customers.length === 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(18, 24, 40, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: '12px'
          }}>
            <div style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              {t('customers.loading')}
            </div>
          </div>
        )}
        {customers && customers.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: '1200px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#AC2000' }}>
                    {[
                      { text: t('customers.tableHeaders.number'), align: 'center', width: '60px' },
                      { text: t('customers.tableHeaders.code'), align: isRTL ? 'right' : 'left', width: '140px' },
                      { text: t('customers.tableHeaders.name'), align: isRTL ? 'right' : 'left', width: '200px' },
                      { text: t('customers.tableHeaders.contactPerson'), align: isRTL ? 'right' : 'left', width: '180px' },
                      { text: t('customers.tableHeaders.email'), align: isRTL ? 'right' : 'left', width: '200px' },
                      { text: t('customers.tableHeaders.phone'), align: isRTL ? 'right' : 'left', width: '140px' },
                      { text: t('customers.tableHeaders.status'), align: 'center', width: '100px' },
                      { text: t('customers.tableHeaders.actions'), align: 'center', width: '200px' }
                    ].map((header, index) => (
                      <th key={index} style={{ padding: '18px 16px', textAlign: header.align, color: 'white', fontWeight: 'bold', fontSize: '14px', width: header.width }}>
                        {header.text}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedCustomers.map((customer, index) => (
                    <tr
                      key={customer.id}
                      style={{
                        borderBottom: '1px solid #333',
                        backgroundColor: index % 2 === 0 ? '#1b3350' : '#1a1f2e',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(ev) => ev.target.closest('tr').style.backgroundColor = '#2a3441'}
                      onMouseLeave={(ev) => ev.target.closest('tr').style.backgroundColor = index % 2 === 0 ? '#1b3350' : '#1a1f2e'}
                    >
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {(currentPageSafe - 1) * perPage + index + 1}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}>{customer.code}</td>
                      <td style={{ padding: '18px 16px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}>
                        {isRTL ? (customer.name_ar || customer.name_en || customer.name || '-') : (customer.name_en || customer.name_ar || customer.name || '-')}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}>{customer.contact_person || '-'}</td>
                      <td style={{ padding: '18px 16px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontSize: '14px', direction: 'ltr' }}>{customer.email || '-'}</td>
                      <td style={{ padding: '18px 16px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}>{customer.phone || '-'}</td>
                      <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                        <span style={{
                          backgroundColor: customer.is_active ? '#AC2000' : '#dc3545',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          display: 'inline-block',
                          whiteSpace: 'nowrap'
                        }}>
                          {customer.is_active ? t('customers.active') : t('customers.inactive')}
                        </span>
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', minWidth: '200px' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                          <ActionButton
                            title={t('customers.preview.single', { defaultValue: 'معاينة PDF' })}
                            onClick={() => handlePreviewCustomer(customer)}
                            bg="#17a2b8"
                          >
                            <FaEye />
                          </ActionButton>
                          <ActionButton
                            title={t('customers.print.single')}
                            onClick={() => handlePrintCustomer(customer)}
                            disabled={isGeneratingPdf}
                            bg={isGeneratingPdf ? '#666' : '#17a2b8'}
                            style={{ opacity: isGeneratingPdf ? 0.6 : 1, cursor: isGeneratingPdf ? 'not-allowed' : 'pointer' }}
                          >
                            <FaPrint />
                          </ActionButton>
                          <ActionButton
                            title={t('customers.export.single')}
                            bg="#28a745"
                            onClick={() => handleExportCustomerToExcel(customer)}
                          >
                            <FaFileExcel />
                          </ActionButton>
                          <ActionButton title={t('customers.actions.edit')} onClick={() => navigate(`/customers/edit/${customer.id}`)}>
                            <FaEdit />
                          </ActionButton>
                          <ActionButton title={t('customers.actions.delete')} bg="#c0392b" onClick={() => handleDelete(customer)}>
                            <FaTrash />
                          </ActionButton>
                          <ActionButton title={t('customers.actions.toggleStatus')} bg="#34495e" onClick={() => handleToggleStatus(customer.id)}>
                            {customer.is_active ? <FaToggleOn /> : <FaToggleOff />}
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination (unified) */}
            {customersPagination && (() => {
              const current = customersPagination.current_page || 1;
              const last = customersPagination.last_page || 1;
              const pages = [];
              for (let i = 1; i <= last; i++) {
                if (i === 1 || i === last || (i >= current - 2 && i <= current + 2)) {
                  pages.push(i);
                } else if (i === current - 3 || i === current + 3) {
                  pages.push('...');
                }
              }
              return (
                <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', borderTop: '1px solid #333' }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, current - 1))}
                    disabled={current === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: current === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: current === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {t('customers.previous')}
                  </button>
                  {pages.map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: p === current ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setCurrentPage(Math.min(last, current + 1))}
                    disabled={current === last}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: current === last ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: current === last ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {t('customers.next')}
                  </button>
                </div>
              );
            })()}
          </>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#888',
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isLoading ? t('customers.loading') : t('customers.noData')}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999
        }}>
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            border: '1px solid #333'
          }}>
            <h2 style={{ color: 'white', marginBottom: '20px', fontSize: '20px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('customers.deleteModal.title')}</h2>
            <p style={{ color: '#ccc', marginBottom: '30px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              {t('customers.deleteModal.message')} "{isRTL ? (customerToDelete?.name_ar || customerToDelete?.name_en || customerToDelete?.name) : (customerToDelete?.name_en || customerToDelete?.name_ar || customerToDelete?.name)}"؟
            </p>
            <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start', gap: '12px', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setCustomerToDelete(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('customers.deleteModal.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('customers.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          PDF MEASUREMENT TEMPLATE — always rendered off-screen.
          buildDynamicPdfChunks reads offsetHeight from these elements to
          compute dynamic page breaks without any fixed row counts.
      ══════════════════════════════════════════════════════════════════ */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', left: '-9999px', top: 0,
          width: '794px', backgroundColor: '#fff',
          padding: '24px', boxSizing: 'border-box',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        <div style={{ border: '2px solid #000', padding: '18px', display: 'flex', flexDirection: 'column' }}>
          <div id="customers-pdf-measure-top-header" style={{ flexShrink: 0 }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt=""
              style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }}
              crossOrigin="anonymous"
            />
            <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                {t('customers.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير العملاء' : 'Customers Report' })}
              </h2>
              <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
              </p>
              {activeFiltersForPdf && (
                <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                  {activeFiltersForPdf}
                </div>
              )}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr id="customers-pdf-measure-thead">
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '40px' }}>#</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '100px' }}>{t('customers.tableHeaders.code')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700' }}>{t('customers.tableHeaders.name')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700' }}>{t('customers.tableHeaders.contactPerson')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', fontWeight: '700' }}>{t('customers.tableHeaders.email')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700' }}>{t('customers.tableHeaders.phone')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px' }}>{t('customers.tableHeaders.status')}</th>
              </tr>
            </thead>
            <tbody id="customers-pdf-measure-tbody">
              <tr>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Sample Code</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>Sample Name</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>Contact Person</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>email@example.com</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>1234567890</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Active</td>
              </tr>
            </tbody>
          </table>
          <div id="customers-pdf-measure-footer" style={{ height: '120px', flexShrink: 0 }} />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          HIDDEN PDF PRINT TEMPLATES
      ══════════════════════════════════════════════════════════════════ */}
      {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
        <div
          key={chunkIndex}
          id={`customers-pdf-all-${chunkIndex}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            height: '1123px',
            backgroundColor: '#ffffff',
            color: '#000000',
            padding: '24px',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}
        >
          <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
            <PdfPageInner
              chunk={chunk.items}
              startIdx={chunk.startIndex}
              chunkIndex={chunkIndex}
              totalChunks={pdfChunks.length}
              showDate={true}
              isRTL={isRTL}
              t={t}
              activeFiltersForPdf={activeFiltersForPdf}
              headerLogo={headerLogo}
              publicUrl={publicUrl}
              dir={dir}
              textAlign={textAlign}
            />
          </div>
        </div>
      ))}

      {/* ══════════════════════════════════════════════════════════════════════
          PDF PREVIEW MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={pdfType === 'single'
          ? t('customers.preview.single', { defaultValue: 'معاينة العميل' })
          : t('customers.title', { defaultValue: 'Customers Report' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfType(null);
          setPdfChunks([]);
        }}
        onPrint={handlePrintFromPreview}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              id={`preview-customers-pdf-all-${chunkIndex}`}
              style={{
                width: '794px',
                height: '1123px',
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '24px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                transform: 'scale(1.0)',
                transformOrigin: 'top center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                position: 'relative'
              }}
            >
              <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
                <PdfPageInner
                  chunk={chunk.items}
                  startIdx={chunk.startIndex}
                  chunkIndex={chunkIndex}
                  totalChunks={pdfChunks.length}
                  showDate={true}
                  isRTL={isRTL}
                  t={t}
                  activeFiltersForPdf={activeFiltersForPdf}
                  headerLogo={headerLogo}
                  publicUrl={publicUrl}
                  dir={dir}
                  textAlign={textAlign}
                />
              </div>
            </div>
          ))}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

const ActionButton = ({ children, onClick, title, bg = '#2f3644', disabled = false, style = {} }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 10px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '38px',
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
  >
    {children}
  </button>
);

const PdfPageInner = ({ chunk, startIdx, chunkIndex, totalChunks, showDate, isRTL, t, activeFiltersForPdf, headerLogo, publicUrl, dir, textAlign }) => (
  <div style={{ border: '2px solid #000', height: '100%', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
    {/* Watermark */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%', flex: 1 }}>
      {/* Header image */}
      <img src={`${publicUrl}/HeaderAce.png`} alt="Header"
        style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }}
        crossOrigin="anonymous"
      />
      {/* Content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, paddingBottom: '120px' }}>
        <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px', flexShrink: 0 }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
            {t('customers.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير العملاء' : 'Customers Report' })}
          </h2>
          <p style={{ fontSize: '11px', color: '#555', margin: '4px 0 0' }}>
            {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
            {totalChunks > 1 && ` — ${t('common.page', { defaultValue: 'Page' })} ${chunkIndex + 1} / ${totalChunks}`}
          </p>
          {activeFiltersForPdf && (
            <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
              {activeFiltersForPdf}
            </div>
          )}
        </div>
        <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'auto' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '40px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>#</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '100px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.code')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.name')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.contactPerson')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.email')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.phone')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('customers.tableHeaders.status')}</th>
              </tr>
            </thead>
            <tbody>
              {chunk.map((customer, idx) => {
                const globalIndex = startIdx + idx + 1;
                const rowBg = idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)';
                return (
                  <tr key={customer.id} style={{ backgroundColor: rowBg }}>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{globalIndex}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{customer.code || customer.id || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: rowBg, whiteSpace: 'normal', maxWidth: '140px', wordBreak: 'break-word' }}>{isRTL ? (customer.name_ar || customer.name_en || customer.name) : (customer.name_en || customer.name_ar || customer.name) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: rowBg }}>{customer.contact_person || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: 'ltr', textAlign: 'left', verticalAlign: 'middle', backgroundColor: rowBg, wordBreak: 'break-all', maxWidth: '100px', whiteSpace: 'normal' }}>{customer.email || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: rowBg, wordBreak: 'break-all', maxWidth: '100px', whiteSpace: 'normal' }}>{customer.phone || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{customer.is_active ? t('customers.active') : t('customers.inactive')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    {/* Footer — absolutely positioned above content (zIndex:2 > content zIndex:1) */}
    <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', zIndex: 2 }}>
      <img src={`${publicUrl}/FooterAce.png`} alt="Footer"
        style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }}
        crossOrigin="anonymous"
      />
    </div>
  </div>
);

export default Customers;
